import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PublicBackground, useToast } from '@app/core';
import { setLocale } from '@app/i18n';
import {
  ParticipationStatus,
  usePaginatedAvailableDatesForReschedule,
  useParticipation,
  usePublicSession,
  useReschedule,
  useUpdateParticipantField,
} from '@app/api';
import {
  MeetingDateSelectionForm,
  MeetingDateSelectionFormValues,
} from './components/MeetingDateSelectionForm';
import { useLoginParticipantWithModal } from '@app/auth';
import { ParticipationEditSummary } from './components/ParticipationEditSummary';

export const ParticipantReschedule = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const toast = useToast();
  const { i18n, t } = useTranslation();
  const login = useLoginParticipantWithModal();

  const { data: participation, isLoading: participationIsLoading } =
    useParticipation(id);
  const { data: session, isLoading: sessionIsLoading } = usePublicSession(
    participation?.sessionLinkId || participation?.sessionId,
  );
  const paginatedAvailableDates = usePaginatedAvailableDatesForReschedule(
    participation?.taskId,
  );

  const { mutate: updateParticipant } = useUpdateParticipantField(
    participation?.participantId,
  );
  const { mutateAsync } = useReschedule(participation?.id);

  const locale = session?.locale;
  useEffect(() => {
    if (locale) {
      setLocale(locale, i18n);
    }
  }, [locale, i18n]);

  const reschedule = async ({
    date,
    timezone,
  }: MeetingDateSelectionFormValues) => {
    if (!participation?.participantId) {
      return;
    }
    try {
      await login(participation.participantId);
      if (timezone) {
        updateParticipant({ fieldName: 'timezone', text: timezone });
      }
      await mutateAsync({
        ...participation,
        date,
      });
      navigate('..');
      toast.saved();
    } catch (e) {
      toast.exception(e);
    }
  };

  if (participation && participation.status !== ParticipationStatus.BOOKED) {
    return <Navigate to=".." />;
  }

  return (
    <PublicBackground
      withMessagesPane
      participation={participation}
      isLoading={participationIsLoading || sessionIsLoading}
    >
      {participation && (
        <>
          <ParticipationEditSummary
            participation={participation}
            title={t('booked.reschedule.title')}
          />

          <MeetingDateSelectionForm
            participation={participation}
            submitText={t('booked.reschedule.submit')}
            onSubmit={reschedule}
            paginatedAvailableDates={paginatedAvailableDates}
          />
        </>
      )}
    </PublicBackground>
  );
};
